import Cookie from 'js-cookie';
import Permutive from 'components/Permutive';
import { AUTH0_KEY_PREFIX } from 'constants/vendor';
import { SITE_KEY } from 'constants/site';

const AUTH0_ID_TOKEN = 'idToken';
const AUTH0_ACCESS_TOKEN = 'accessToken';
const USER_DATA = 'user';

const COOKIE_OPTS = { expires: 30 };

const REDIRECT_KEY = 'on-auth-redirect';

/**
 * handles slight reformatting/reshaping of auth0 data to fit our components and
 * graphql query
 *
 * @export
 * @param {Object} [data={}] user data from auth0
 * @returns {Object} user data for our redux store & components
 */
export function normalizeUserData(data = {}) {
  // using direct map to avoid use of camelcase dep
  const keyMap = {
    at_hash: 'atHash',
    creation_complete: 'creationComplete',
    community_id: 'communityId',
    display_name: 'displayName',
    email_verified: 'emailVerified',
    originating_site: 'originatingSite',
    picture: 'image',
    sub: 'auth0User',
    atid: 'atid',
    updated_at: 'updatedAt'
  };

  const user = Object.entries(data).reduce((acc, [k, v]) => {
    // remove the URL prefix on our some keys and convert to camelCase
    const key = k.replace(AUTH0_KEY_PREFIX, '');

    // map/rename keys
    const newKey = keyMap[key] ? keyMap[key] : key;

    return { ...acc, ...{ [newKey]: v } };
  }, {});

  return {
    ...user,
    // set empty displayNames to nickname
    ...(!user.displayName && {
      displayName: user.nickname
    }),
    // retain legacy value, use SITE_KEY for new users
    ...(!user.originatingSite && {
      originatingSite: SITE_KEY
    })
  };
}

export function setAuthCookies(idToken, accessToken, userData) {
  if (idToken) {
    Cookie.set(AUTH0_ID_TOKEN, idToken, COOKIE_OPTS);
  }
  if (accessToken) {
    Cookie.set(AUTH0_ACCESS_TOKEN, accessToken, COOKIE_OPTS);
  }
  if (userData) {
    Cookie.set(USER_DATA, userData, COOKIE_OPTS);
  }
}

export function clearAuthCookies() {
  Cookie.remove(AUTH0_ID_TOKEN);
  Cookie.remove(AUTH0_ACCESS_TOKEN);
  Cookie.remove(USER_DATA);
}

/**
 * Update User cookies to include the atid and also identify user in Permutive
 */
export async function updateUserData({ idToken, accessToken, user, atid }) {
  setAuthCookies(idToken, accessToken, {
    ...user,
    atid
  });
  // Identify user in Permutive
  // Note, Permutive.identify uses Promise.race so that sign will be
  // successful even if this call fails or has high latency, i.e.
  // there is a timeout
  try {
    await Permutive.identify(atid);
  } catch (error) {
    console.log(error); // eslint-disable-line no-console
  }
}

/**
 * reads user data cookie and returns properly formatted object.
 *
 * returns null if cookie does not exist.
 *
 * @export
 * @returns {Object} user data object (could be null)
 */
export function getUserFromCookie() {
  const userData = Cookie.getJSON(USER_DATA);
  // CLEANUP: remove normalization on get after we can be sure all old-style
  // (non-normalized) cookies are expired.
  return userData ? normalizeUserData(userData) : null;
}

export function setRedirectUrl(url) {
  return window.localStorage.setItem(REDIRECT_KEY, url);
}

export function getRedirectUrl() {
  return window.localStorage.getItem(REDIRECT_KEY) || window.location.origin;
}

export function clearRedirectUrl() {
  window.localStorage.removeItem(REDIRECT_KEY);
}

export function getAuthTokens() {
  return {
    idToken: Cookie.get(AUTH0_ID_TOKEN),
    accessToken: Cookie.get(AUTH0_ACCESS_TOKEN)
  };
}
